import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CustomerData } from "@/types/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Loader2, ChevronDown, ChevronUp, Ban, User, Building2, MapPin, Shield, Wheat, Tags } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { toast } from "@/hooks/use-toast";


const clientSchema = z.object({
  firstName: z.string().trim().max(50).optional().or(z.literal("")),
  lastName: z.string().trim().max(50).optional().or(z.literal("")),
  email: z.string().trim().email("Email invalide").max(255).optional().or(z.literal("")),
  phone: z.string().trim().max(20).optional().or(z.literal("")),
  notes: z.string().max(1000).optional().or(z.literal("")),
  // New fields
  civility: z.string().optional().or(z.literal("")),
  company: z.string().max(100).optional().or(z.literal("")),
  professionalEmail: z.string().email("Email professionnel invalide").max(255).optional().or(z.literal("")),
  professionalPhone: z.string().max(20).optional().or(z.literal("")),
  birthdate: z.string().optional().or(z.literal("")),
  address: z.string().max(255).optional().or(z.literal("")),
  city: z.string().max(100).optional().or(z.literal("")),
  zip: z.string().max(10).optional().or(z.literal("")),
  country: z.string().max(100).optional().or(z.literal("")),
  lang: z.string().optional().or(z.literal("")),
  allergiesNotes: z.string().max(1000).optional().or(z.literal("")),
  allergiesTags: z.string().optional().or(z.literal("")), // comma-separated
  guestTags: z.string().optional().or(z.literal("")), // comma-separated
  guestStatus: z.string().optional().or(z.literal("")),
  isBlacklisted: z.boolean().optional(),
  hasNoShow: z.boolean().optional(),
}).refine(
  data =>
    (data.firstName && data.firstName.length >= 1) ||
    (data.lastName && data.lastName.length >= 1) ||
    (data.email && data.email.length >= 1) ||
    (data.phone && data.phone.length >= 1),
  {
    message: "Au moins un champ d'identification est requis (prénom, nom, email ou téléphone)",
    path: ["firstName"],
  }
);

type ClientFormData = z.infer<typeof clientSchema>;

interface EditClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: CustomerData;
  onClientUpdate: (oldEmail: string, updates: Partial<CustomerData>) => void;
}

function SectionHeader({ icon: Icon, title, expanded, onToggle }: { icon: any; title: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
    >
      <span className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        {title}
      </span>
      {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </button>
  );
}

export function EditClientDialog({
  open,
  onOpenChange,
  client,
  onClientUpdate,
}: EditClientDialogProps) {
  const { establishmentId } = useEstablishment();
  const [isUpdating, setIsUpdating] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    identity: true,
    professional: false,
    address: false,
    status: false,
    allergies: false,
    tags: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      firstName: client.firstName,
      lastName: client.lastName,
      email: client.email,
      phone: client.phone || "",
      notes: client.notes || "",
      civility: client.civility || "",
      company: client.company || "",
      professionalEmail: client.professionalEmail || "",
      professionalPhone: client.professionalPhone || "",
      birthdate: client.birthdate || "",
      address: client.address || "",
      city: client.city || "",
      zip: client.zip || "",
      country: client.country || "",
      lang: client.lang || "fr",
      allergiesNotes: client.allergiesNotes || "",
      allergiesTags: (client.allergiesTags || []).join(", "),
      guestTags: (client.guestTags || []).join(", "),
      guestStatus: client.guestStatus || "",
      isBlacklisted: client.isBlacklisted || false,
      hasNoShow: client.hasNoShow || false,
    },
  });

  const watchedEmail = form.watch("email");
  const emailHasChanged = (watchedEmail || "").toLowerCase().trim() !== (client.email || "").toLowerCase();

  useEffect(() => {
    if (open) {
      form.reset({
        firstName: client.firstName,
        lastName: client.lastName,
        email: client.email,
        phone: client.phone || "",
        notes: client.notes || "",
        civility: client.civility || "",
        company: client.company || "",
        professionalEmail: client.professionalEmail || "",
        professionalPhone: client.professionalPhone || "",
        birthdate: client.birthdate || "",
        address: client.address || "",
        city: client.city || "",
        zip: client.zip || "",
        country: client.country || "",
        lang: client.lang || "fr",
        allergiesNotes: client.allergiesNotes || "",
        allergiesTags: (client.allergiesTags || []).join(", "),
        guestTags: (client.guestTags || []).join(", "),
        guestStatus: client.guestStatus || "",
        isBlacklisted: client.isBlacklisted || false,
        hasNoShow: client.hasNoShow || false,
      });
      setEmailError(null);
    }
  }, [open, client, form]);

  useEffect(() => {
    if (emailError) setEmailError(null);
  }, [watchedEmail]);

  const onSubmit = async (data: ClientFormData) => {
    setIsUpdating(true);
    setEmailError(null);

    try {
      const normalizedNewEmail = (data.email || "").toLowerCase().trim();
      const normalizedOldEmail = (client.email || "").toLowerCase();

      // Only use the edge function when both old and new emails are non-empty and different,
      // and we have a known customer id (the edge function requires customerId).
      if (normalizedNewEmail && normalizedOldEmail && normalizedNewEmail !== normalizedOldEmail && client.id) {
        const { data: result, error } = await supabase.functions.invoke('update-customer-email', {
          body: {
            customerId: client.id,
            newEmail: normalizedNewEmail,
            establishmentId
          }
        });

        if (error) {
          setEmailError(error.message || "Erreur lors de la mise à jour de l'email");
          return;
        }
        if (!result?.success) {
          setEmailError(result?.error || "Erreur lors de la mise à jour de l'email");
          return;
        }

        toast({
          title: "Email mis à jour",
          description: `L'email a été modifié de ${normalizedOldEmail} vers ${normalizedNewEmail}`,
        });
      }

      // Parse comma-separated tags
      const parseTags = (str: string | undefined): string[] =>
        (str || "").split(",").map(t => t.trim()).filter(Boolean);

      onClientUpdate(client.email || "", {
        id: client.id,
        ...data,
        email: normalizedNewEmail,
        allergiesTags: parseTags(data.allergiesTags),
        guestTags: parseTags(data.guestTags),
      });
      onOpenChange(false);

    } catch (error) {
      console.error('[EditClientDialog] Unexpected error:', error);
      setEmailError("Une erreur inattendue s'est produite");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Modifier le client</DialogTitle>
          <DialogDescription>
            Modifiez les informations du client.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto max-h-[65vh] pr-4">
          <Form {...form}>
            <form id="edit-client-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* === Identity Section (always visible) === */}
              <div className="grid grid-cols-3 gap-3">
                <FormField
                  control={form.control}
                  name="civility"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Civilité</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={isUpdating}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="mr">M.</SelectItem>
                          <SelectItem value="mrs">Mme</SelectItem>
                          <SelectItem value="other">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prénom</FormLabel>
                      <FormControl><Input placeholder="Jean" {...field} disabled={isUpdating} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom</FormLabel>
                      <FormControl><Input placeholder="Dupont" {...field} disabled={isUpdating} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl><Input type="email" {...field} disabled={isUpdating} /></FormControl>
                    <FormMessage />
                    {emailError && <p className="text-sm text-destructive mt-1">{emailError}</p>}
                  </FormItem>
                )}
              />

              {emailHasChanged && (
                <Alert variant="default" className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 dark:text-amber-200">
                    La modification de l'email affectera toutes les futures communications.
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Téléphone</FormLabel>
                      <FormControl><Input type="tel" {...field} disabled={isUpdating} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lang"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Langue</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={isUpdating}>
                        <FormControl>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="fr">Français</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="de">Deutsch</SelectItem>
                          <SelectItem value="it">Italiano</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes client</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Préférences, notes importantes..." className="min-h-[60px] resize-none" {...field} disabled={isUpdating} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              {/* === Status & Flags === */}
              <SectionHeader icon={Shield} title="Statut & Signalements" expanded={expandedSections.status} onToggle={() => toggleSection('status')} />
              {expandedSections.status && (
                <div className="space-y-3 pl-1">
                  <FormField
                    control={form.control}
                    name="guestStatus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Statut client</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={isUpdating}>
                          <FormControl>
                            <SelectTrigger><SelectValue placeholder="Non défini" /></SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="new">Nouveau</SelectItem>
                            <SelectItem value="returning">Client récurrent</SelectItem>
                            <SelectItem value="vip">VIP</SelectItem>
                            <SelectItem value="press">Presse</SelectItem>
                            <SelectItem value="influencer">Influenceur</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Ban className="h-4 w-4 text-destructive" />
                      <span className="text-sm">Client blacklisté</span>
                    </div>
                    <FormField
                      control={form.control}
                      name="isBlacklisted"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isUpdating} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span className="text-sm">A fait un no-show</span>
                    </div>
                    <FormField
                      control={form.control}
                      name="hasNoShow"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isUpdating} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              <Separator />

              {/* === Allergies & Tags === */}
              <SectionHeader icon={Wheat} title="Allergies & Préférences" expanded={expandedSections.allergies} onToggle={() => toggleSection('allergies')} />
              {expandedSections.allergies && (
                <div className="space-y-3 pl-1">
                  <FormField
                    control={form.control}
                    name="allergiesNotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes allergies</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Intolérance au gluten, allergie aux noix..." className="min-h-[60px] resize-none" {...field} disabled={isUpdating} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="allergiesTags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags allergies (séparés par des virgules)</FormLabel>
                        <FormControl><Input placeholder="gluten, noix, lactose" {...field} disabled={isUpdating} /></FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="guestTags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags client (séparés par des virgules)</FormLabel>
                        <FormControl><Input placeholder="habitué, brunch, terrasse" {...field} disabled={isUpdating} /></FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <Separator />

              {/* === Professional === */}
              <SectionHeader icon={Building2} title="Professionnel" expanded={expandedSections.professional} onToggle={() => toggleSection('professional')} />
              {expandedSections.professional && (
                <div className="space-y-3 pl-1">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Entreprise</FormLabel>
                        <FormControl><Input {...field} disabled={isUpdating} /></FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="professionalEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email pro</FormLabel>
                          <FormControl><Input type="email" {...field} disabled={isUpdating} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="professionalPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tél. pro</FormLabel>
                          <FormControl><Input type="tel" {...field} disabled={isUpdating} /></FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              <Separator />

              {/* === Address === */}
              <SectionHeader icon={MapPin} title="Adresse & Localisation" expanded={expandedSections.address} onToggle={() => toggleSection('address')} />
              {expandedSections.address && (
                <div className="space-y-3 pl-1">
                  <FormField
                    control={form.control}
                    name="birthdate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date de naissance</FormLabel>
                        <FormControl><Input type="date" {...field} disabled={isUpdating} /></FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Adresse</FormLabel>
                        <FormControl><Input {...field} disabled={isUpdating} /></FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <FormField
                      control={form.control}
                      name="zip"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>NPA</FormLabel>
                          <FormControl><Input {...field} disabled={isUpdating} /></FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ville</FormLabel>
                          <FormControl><Input {...field} disabled={isUpdating} /></FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pays</FormLabel>
                          <FormControl><Input {...field} disabled={isUpdating} /></FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}
            </form>
          </Form>
        </div>
        <DialogFooter className="pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            Annuler
          </Button>
          <Button type="submit" form="edit-client-form" disabled={isUpdating}>
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              "Enregistrer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
